import { lazy, useMemo, type FC } from 'react';
import { useParams } from 'react-router-dom';
import type { Uuid } from 'src/apis/api-utilities';
import type { HydrogenNetworkPointOrBundle } from 'src/apis/hydrogen-fcfs/types';
import { useHydrogenFcfsRootLink } from 'src/apis/hydrogen-fcfs/use-hydrogen-fcfs-root';
import { useHydrogenNetworkPointCapacities } from 'src/apis/hydrogen-fcfs/use-hydrogen-network-point-capacities';
import { useHydrogenNetworkPointOrBundle } from 'src/apis/hydrogen-fcfs/use-hydrogen-network-point-or-bundle';
import { useHydrogenNetworkPointPrices } from 'src/apis/hydrogen-fcfs/use-hydrogen-network-point-prices';
import { PageSpinner } from 'src/components/spinner-container';
import { useHydrogenCapacityPageParams } from 'src/pages/h2/network-points/details/use-capacity-page-params';
import { useHydrogenNetworkPointPageParams } from 'src/pages/h2/network-points/details/use-page-params';

const Page = lazy(() => import('./page'));

export const NetworkPointDetails: FC = () => {
  const params = useParams<{ networkPointOrBundleId: Uuid }>();
  const url = useHydrogenFcfsRootLink('networkPointDetails');

  const networkPointOrBundleRequest = useHydrogenNetworkPointOrBundle({
    url,
    networkPointOrBundleId: params.networkPointOrBundleId,
  });

  if (!networkPointOrBundleRequest.response) return <PageSpinner />;

  const networkPointOrBundle = networkPointOrBundleRequest.response.data;

  return networkPointOrBundle.direction === 'BUNDLE' ? (
    <NetworkPointDetailsWithBundle
      networkPointOrBundle={networkPointOrBundle}
    />
  ) : (
    <NetworkPointDetailsWithNetworkPoint
      networkPointOrBundle={networkPointOrBundle}
    />
  );
};

const NetworkPointDetailsWithBundle: FC<{
  networkPointOrBundle: HydrogenNetworkPointOrBundle;
}> = ({ networkPointOrBundle }) => {
  return <Page networkPointOrBundle={networkPointOrBundle} />;
};

const NetworkPointDetailsWithNetworkPoint: FC<{
  networkPointOrBundle: HydrogenNetworkPointOrBundle;
}> = ({ networkPointOrBundle }) => {
  const networkPoint = networkPointOrBundle.networkPoints[0];
  const capacityPageParams = useHydrogenCapacityPageParams();
  const pageParams = useHydrogenNetworkPointPageParams(networkPoint);

  const capacitiesParams = useMemo(
    () => ({
      validFrom: capacityPageParams.value.validFrom,
      validTo: capacityPageParams.value.validTo,
      capacityUsageGranularity:
        capacityPageParams.value.capacityUsageGranularity,
      capacityCategory: pageParams.value.capacityCategory,
    }),
    [
      capacityPageParams.value.validFrom,
      capacityPageParams.value.validTo,
      capacityPageParams.value.capacityUsageGranularity,
      pageParams.value.capacityCategory,
    ]
  );

  const capacitiesRequest = useHydrogenNetworkPointCapacities({
    url: networkPoint._links?.networkPointGetCapacities?.href,
    params: capacitiesParams,
  });

  const pricesParams = useMemo(
    () => ({
      capacityCategory: pageParams.value.capacityCategory,
    }),
    [pageParams.value.capacityCategory]
  );
  const pricesRequest = useHydrogenNetworkPointPrices({
    url: networkPoint._links?.networkPointGetPrices?.href,
    params: pricesParams,
  });

  if (
    (capacitiesRequest && !capacitiesRequest.response) ||
    (pricesRequest && !pricesRequest.response)
  )
    return <PageSpinner />;

  return (
    <Page
      networkPointOrBundle={networkPointOrBundle}
      pageParams={pageParams}
      capacityPageParams={capacityPageParams}
      capacitiesRequest={capacitiesRequest}
      pricesRequest={pricesRequest}
    />
  );
};
